package com.patronat.top.ui.atoms

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.dp
import com.patronat.top.ui.theme.*

enum class ButtonType {
    BrownFilled,
    BrownOutline,
    Black,
    BlackOutline,
    Error,
    ErrorOutline
}

@Composable
fun AppButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    type: ButtonType = ButtonType.BrownFilled,
    enabled: Boolean = true,
    isLoading: Boolean = false,
    leadingIcon: Painter? = null,
    trailingIcon: Painter? = null
) {
    val interactionSource = remember { MutableInteractionSource() }

    val isPressed by interactionSource.collectIsPressedAsState()
    val isHovered by interactionSource.collectIsHoveredAsState()

    val (containerColor, contentColor, borderColor) = when {
        // 1. DISABLED
        !enabled -> when (type) {
            // Filled buttons -> Grey Background, White Text
            ButtonType.BrownFilled, ButtonType.Black, ButtonType.Error ->
                Triple(MineShaft500, White, null)

            // Outline buttons -> Transparent Background, Grey Text, Grey Border
            ButtonType.BrownOutline, ButtonType.BlackOutline, ButtonType.ErrorOutline ->
                Triple(Color.Transparent, MineShaft500, MineShaft100)
        }

        // 2. PRESSED
        isPressed -> when (type) {
            ButtonType.BrownFilled -> Triple(ButtonBrownFilledBgPressed, ButtonBrownFilledContent, null)
            ButtonType.BrownOutline -> Triple(Color.Transparent, ButtonBrownOutlineContentPressed, ButtonBrownOutlineContentPressed)

            ButtonType.Black -> Triple(ButtonBlackBgPressed, ButtonBlackContent, null)
            ButtonType.BlackOutline -> Triple(ButtonBlackOutlineBgPressed, ButtonBlackOutlineContent, ButtonBlackOutlineBorder)

            ButtonType.Error -> Triple(ButtonErrorBgPressed, ButtonErrorContent, null)
            ButtonType.ErrorOutline -> Triple(ButtonErrorOutlineBgPressed, ButtonErrorOutlineContent, ButtonErrorOutlineBorder)
        }

        // 3. HOVER
        isHovered -> when (type) {
            ButtonType.BrownFilled -> Triple(ButtonBrownFilledBgHover, ButtonBrownFilledContent, null)
            ButtonType.BrownOutline -> Triple(Color.Transparent, ButtonBrownOutlineContentHover, ButtonBrownOutlineContentHover)

            ButtonType.Black -> Triple(ButtonBlackBgHover, ButtonBlackContent, null)
            ButtonType.BlackOutline -> Triple(ButtonBlackOutlineBgHover, ButtonBlackOutlineContent, ButtonBlackOutlineBorder)

            ButtonType.Error -> Triple(ButtonErrorBgHover, ButtonErrorContent, null)
            ButtonType.ErrorOutline -> Triple(ButtonErrorOutlineBgHover, ButtonErrorOutlineContent, ButtonErrorOutlineBorder)
        }

        // 4. DEFAULT
        else -> when (type) {
            ButtonType.BrownFilled -> Triple(ButtonBrownFilledBgDefault, ButtonBrownFilledContent, null)
            ButtonType.BrownOutline -> Triple(Color.Transparent, ButtonBrownOutlineContentDefault, ButtonBrownOutlineContentDefault)

            ButtonType.Black -> Triple(ButtonBlackBgDefault, ButtonBlackContent, null)
            ButtonType.BlackOutline -> Triple(Color.Transparent, ButtonBlackOutlineContent, ButtonBlackOutlineBorder)

            ButtonType.Error -> Triple(ButtonErrorBgDefault, ButtonErrorContent, null)
            ButtonType.ErrorOutline -> Triple(Color.Transparent, ButtonErrorOutlineContent, ButtonErrorOutlineBorder)
        }
    }

    val contentPadding = PaddingValues(horizontal = 24.dp, vertical = 8.dp)

    val shape = RoundedCornerShape(16.dp)
    val borderStroke = borderColor?.let { BorderStroke(1.dp, it) }

    Box(
        modifier = modifier
            .height(48.dp)
            .defaultMinSize(minWidth = 64.dp, minHeight = 48.dp)
            .clip(shape)
            .background(containerColor)
            .then(
                if (borderStroke != null) {
                    Modifier.border(borderStroke, shape)
                } else {
                    Modifier
                }
            )
            .clickable(
                enabled = enabled && !isLoading,
                interactionSource = interactionSource,
                indication = LocalIndication.current,
                onClick = onClick
            )
            .padding(contentPadding),
        contentAlignment = Alignment.Center
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(24.dp),
                color = contentColor,
                strokeWidth = 2.5.dp
            )
        } else {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center
            ) {
                if (leadingIcon != null) {
                    Icon(
                        painter = leadingIcon,
                        contentDescription = null,
                        modifier = Modifier.size(24.dp),
                        tint = contentColor
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                }

                Text(
                    text = text,
                    style = ButtonTextStyle,
                    color = contentColor
                )

                if (trailingIcon != null) {
                    Spacer(modifier = Modifier.width(8.dp))
                    Icon(
                        painter = trailingIcon,
                        contentDescription = null,
                        modifier = Modifier.size(24.dp),
                        tint = contentColor
                    )
                }
            }
        }
    }
}
